import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../lib/session.js';
import { LanguageToggle } from '../components/LanguageToggle.js';

/**
 * Sign in.
 *
 * One error message for every failure, matching the API: distinguishing "no
 * such account" from "wrong password" turns this form into a way to enumerate
 * staff addresses.
 */
export function LoginScreen() {
  const { t } = useTranslation();
  const { signIn } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailed(false);
    try {
      await signIn(email, password);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col items-center gap-3 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 w-auto"
            width={262}
            height={300}
          />
          <p className="text-sm text-brand-teal">{t('app.subtitle')}</p>
        </div>

        <form className="mt-6 flex flex-col gap-3" onSubmit={submit}>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('auth.email')}</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('auth.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2"
            />
          </label>

          {failed ? (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {t('auth.failed')}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="mt-2 rounded-md bg-brand-teal px-4 py-2 font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
          >
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        <div className="mt-6 flex justify-center">
          <LanguageToggle />
        </div>
      </div>
    </main>
  );
}
